<script lang="ts">
  import { version as runningBuildIdentity } from '$app/environment';
  import { onMount } from 'svelte';
  import { _ } from 'svelte-i18n';

  import {
    fetchAppVersion,
    parseLoadedBuildVersion,
    type AppComponentVersion,
    type AppVersionResponse
  } from '$lib/api/app-version';
  import { Button } from '$lib/components/ui/button/index.js';
  import * as Dialog from '$lib/components/ui/dialog/index.js';
  import { formatDateTime } from '$lib/utils/format';

  import { createAppVersionDetailsState } from './app-version-details-state.svelte';

  type Props = {
    buildIdentity?: string;
    load?: () => Promise<AppVersionResponse>;
  };

  let {
    buildIdentity = runningBuildIdentity,
    load = fetchAppVersion
  }: Props = $props();

  const loadedBuild = $derived(parseLoadedBuildVersion(buildIdentity));
  const versionState = createAppVersionDetailsState({ load: () => load() });

  onMount(() => {
    void versionState.refresh();
  });

  function componentIdentity(component: AppComponentVersion): string {
    if (!component.available || !component.version) {
      return $_('app.version.unavailable');
    }

    return component.commit
      ? `${component.version}+${component.commit}`
      : component.version;
  }

  function loadedFrontendMatches(details: AppVersionResponse): boolean {
    return details.frontend.available
      && details.frontend.version === loadedBuild.version
      && details.frontend.commit === loadedBuild.commit;
  }
</script>

<Dialog.Root>
  <Dialog.Trigger>
    {#snippet child({ props })}
      <Button
        {...props}
        variant="ghost"
        size="sm"
        class="h-6 px-1.5 text-[0.7rem] font-normal text-muted-foreground hover:text-foreground"
        aria-label={$_('app.version.open', { values: { version: loadedBuild.version } })}
      >
        v{loadedBuild.version}
      </Button>
    {/snippet}
  </Dialog.Trigger>

  <Dialog.Content class="sm:max-w-md">
    <Dialog.Header>
      <Dialog.Title>{$_('app.version.title')}</Dialog.Title>
      <Dialog.Description>{$_('app.version.description')}</Dialog.Description>
    </Dialog.Header>

    <div class="space-y-3 text-sm">
      <div class="flex items-start justify-between gap-4 border-b border-border pb-2">
        <span class="text-muted-foreground">{$_('app.version.frontend')}</span>
        <div class="min-w-0 text-right font-mono text-xs">
          <div class="break-all">{loadedBuild.identity}</div>
          {#if versionState.details && !loadedFrontendMatches(versionState.details)}
            <div class="mt-1 break-all text-muted-foreground">
              {$_('app.version.serverExpected')}: {componentIdentity(versionState.details.frontend)}
            </div>
          {/if}
        </div>
      </div>

      {#if versionState.details}
        <div class="flex items-start justify-between gap-4 border-b border-border pb-2">
          <span class="text-muted-foreground">{$_('app.version.webInterface')}</span>
          <span class="break-all text-right font-mono text-xs">
            {componentIdentity(versionState.details.webInterface)}
          </span>
        </div>
        <div class="flex items-start justify-between gap-4 border-b border-border pb-2">
          <span class="text-muted-foreground">{$_('app.version.core')}</span>
          <span class="break-all text-right font-mono text-xs">
            {componentIdentity(versionState.details.core)}
          </span>
        </div>

        {#if versionState.details.deployedAtUtc}
          <div class="flex items-center justify-between gap-4">
            <span class="text-muted-foreground">{$_('app.version.deployedAt')}</span>
            <span>{formatDateTime(versionState.details.deployedAtUtc)}</span>
          </div>
        {/if}
        {#if versionState.details.core.startedAtUtc}
          <div class="flex items-center justify-between gap-4">
            <span class="text-muted-foreground">{$_('app.version.coreStartedAt')}</span>
            <span>{formatDateTime(versionState.details.core.startedAtUtc)}</span>
          </div>
        {/if}

        <p class={versionState.details.core.available && versionState.details.compatible && loadedFrontendMatches(versionState.details)
          ? 'text-xs text-emerald-700 dark:text-emerald-300'
          : 'text-xs text-amber-700 dark:text-amber-300'}>
          {#if !versionState.details.core.available}
            {$_('app.version.coreUnavailable')}
          {:else if versionState.details.compatible && loadedFrontendMatches(versionState.details)}
            {$_('app.version.compatible')}
          {:else}
            {$_('app.version.mismatch')}
          {/if}
        </p>
      {:else if versionState.status === 'loading'}
        <p class="text-muted-foreground">{$_('app.version.loading')}</p>
      {:else if versionState.status === 'error'}
        <p class="text-amber-700 dark:text-amber-300">{$_('app.version.loadFailed')}</p>
      {/if}

      {#if versionState.status === 'error' && versionState.details}
        <p class="text-xs text-amber-700 dark:text-amber-300">{$_('app.version.loadFailed')}</p>
      {/if}
    </div>

    <Dialog.Footer>
      <Button
        variant="outline"
        disabled={versionState.status === 'loading'}
        onclick={() => void versionState.refresh()}
      >
        {$_('app.version.refresh')}
      </Button>
    </Dialog.Footer>
  </Dialog.Content>
</Dialog.Root>
